import { useTranslation } from 'react-i18next';
import type { ProcOutput } from '@pz/server';
import { Field, Select } from './ui';

type ProjectData = ProcOutput<'projects.get'>;

/** Filter by Forma category or type and by the custom fields taken over ('' = all). */
export interface TaskFieldFilter {
  /** 'c:<category id>', 't:<type id>' or '' */
  kind: string;
  /** field id -> value; NONE_VALUE = without value */
  attributes: Record<string, string>;
}

export const EMPTY_FIELD_FILTER: TaskFieldFilter = { kind: '', attributes: {} };
export const NONE_VALUE = '__none__';

/** Input of tasks.list for the field filter. */
export function fieldFilterInput(f: TaskFieldFilter) {
  const attributes = Object.entries(f.attributes)
    .filter(([, v]) => v)
    .map(([id, v]) => ({ id, value: v === NONE_VALUE ? '' : v }));
  return {
    issueTypeId: f.kind.startsWith('c:') ? f.kind.slice(2) : undefined,
    issueSubtypeId: f.kind.startsWith('t:') ? f.kind.slice(2) : undefined,
    attributes: attributes.length ? attributes : undefined,
  };
}

export function fieldFilterActive(f: TaskFieldFilter) {
  return !!f.kind || Object.values(f.attributes).some(Boolean);
}

/**
 * Selects for the Forma category/type and for every custom field taken over (Gewerk, Geschoss, ...). Each list
 * can be searched by typing; values come from the tasks of the project.
 */
export function TaskFieldFilters({ data, value, onChange }: { data: ProjectData; value: TaskFieldFilter; onChange: (v: TaskFieldFilter) => void }) {
  const { t } = useTranslation();
  const fields = data.attributeDefs.filter((a) => a.mirrored && !a.formaDeleted);
  const categories = data.issueTypes;
  return (
    <>
      {categories.length > 0 && (
        <Field label={t('tasks.formaKind')} className="w-60">
          <Select className="h-8" value={value.kind} onChange={(e) => onChange({ ...value, kind: e.target.value })} aria-label={t('tasks.formaKind')}>
            <option value="">{t('tasks.allKinds')}</option>
            {categories.map((c) => (
              <optgroup key={c.id} label={c.title}>
                <option value={`c:${c.id}`}>{t('tasks.wholeCategory', { category: c.title })}</option>
                {c.types.map((st) => (
                  <option key={st.id} value={`t:${st.id}`}>
                    {st.excluded ? t('tasks.typeExcluded', { type: st.title }) : st.title}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </Field>
      )}
      {fields.map((f) => (
        <Field key={f.id} label={f.title} className="w-44">
          <Select
            className="h-8"
            aria-label={f.title}
            value={value.attributes[f.id] ?? ''}
            onChange={(e) => onChange({ ...value, attributes: { ...value.attributes, [f.id]: e.target.value } })}
          >
            <option value="">{t('common.all')}</option>
            {(data.attributeValues[f.id] ?? []).map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
            <option value={NONE_VALUE}>{t('tasks.withoutValue')}</option>
          </Select>
        </Field>
      ))}
    </>
  );
}
